import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  BookOpen,
  Users,
  MapPin,
  Route,
  Settings,
  FileText,
  HelpCircle,
  ChevronDown,
  ChevronRight,
  Play,
  CheckCircle,
  AlertCircle,
  Info,
  Shield,
  UserCheck,
  Cog,
  MessageCircle,
  Search
} from 'lucide-react'

const ManualUsuario = () => {
  const navigate = useNavigate()
  const [seccionesAbiertas, setSeccionesAbiertas] = useState({
    introduccion: true,
    gestionClientes: false,
    simulacionesGPS: false,
    replicasGPS: false,
    generarRutas: false,
    simulacionPosicion: false,
    sistemaPermisos: false,
    gestionUsuarios: false,
    configuracion: false,
    faq: false,
    glosario: false,
    consejos: false
  })

  const toggleSeccion = (seccion) => {
    setSeccionesAbiertas(prev => ({
      ...prev,
      [seccion]: !prev[seccion]
    }))
  }

  const Seccion = ({ titulo, icono, abierta, onToggle, children }) => (
    <div className="border border-gray-200 rounded-lg mb-4">
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 text-left bg-gray-50 hover:bg-gray-100 flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          {icono}
          <h3 className="text-lg font-semibold text-gray-900">{titulo}</h3>
        </div>
        {abierta ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
      </button>
      {abierta && (
        <div className="p-6 bg-white">
          {children}
        </div>
      )}
    </div>
  )

  const Paso = ({ numero, titulo, descripcion, imagen, consejo }) => (
    <div className="mb-6 p-4 border-l-4 border-blue-500 bg-blue-50">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
          {numero}
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900 mb-2">{titulo}</h4>
          <p className="text-gray-700 mb-2">{descripcion}</p>
          {imagen && (
            <div className="mt-3 p-3 bg-white rounded border">
              <img src={imagen} alt={titulo} className="max-w-full h-auto rounded" />
            </div>
          )}
          {consejo && (
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <div className="flex items-center gap-2 text-yellow-800">
                <Info className="w-4 h-4" />
                <span className="text-sm font-medium">Consejo:</span>
              </div>
              <p className="text-sm text-yellow-700 mt-1">{consejo}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  const Caracteristica = ({ titulo, descripcion, icono }) => (
    <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
      <div className="flex-shrink-0 w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center">
        {icono}
      </div>
      <div>
        <h4 className="font-semibold text-gray-900 mb-1">{titulo}</h4>
        <p className="text-sm text-gray-700">{descripcion}</p>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Manual de Usuario</h1>
                <p className="text-gray-600">Guía completa del sistema Tecnytte Admin</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Volver al Dashboard
            </button>
          </div>
        </div>
      </div>

      {/* Contenido */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Introducción */}
        <Seccion
          titulo="Introducción al Sistema"
          icono={<HelpCircle className="w-6 h-6" />}
          abierta={seccionesAbiertas.introduccion}
          onToggle={() => toggleSeccion('introduccion')}
        >
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-blue-800 mb-2">
                <Info className="w-5 h-5" />
                <span className="font-semibold">Bienvenido al Sistema Tecnytte Admin</span>
              </div>
              <p className="text-blue-700">
                Este sistema integra la gestión de clientes y simulaciones GPS en una plataforma moderna y fácil de usar. 
                Diseñado para optimizar el trabajo diario con herramientas intuitivas y funcionalidades avanzadas.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Caracteristica
                titulo="Gestión de Clientes"
                descripcion="Administra información de clientes, estados de pago y seguimiento de deudas"
                icono={<Users className="w-4 h-4" />}
              />
              <Caracteristica
                titulo="Simulaciones GPS"
                descripcion="Crea y gestiona simulaciones de rutas GPS con diferentes tipos de configuración"
                icono={<MapPin className="w-4 h-4" />}
              />
              <Caracteristica
                titulo="Generación de Rutas"
                descripcion="Genera rutas automáticas entre puntos usando servicios de navegación"
                icono={<Route className="w-4 h-4" />}
              />
              <Caracteristica
                titulo="Sistema de Permisos"
                descripcion="Control granular de acceso basado en roles y permisos específicos"
                icono={<Shield className="w-4 h-4" />}
              />
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-800 mb-2">
                <CheckCircle className="w-5 h-5" />
                <span className="font-semibold">Características Principales</span>
              </div>
              <ul className="text-green-700 space-y-1 text-sm">
                <li>• Interfaz moderna y responsive</li>
                <li>• Filtros avanzados y búsqueda en tiempo real</li>
                <li>• Actualización masiva de registros</li>
                <li>• Exportación de datos</li>
                <li>• Sistema de notificaciones</li>
                <li>• Integración con mapas interactivos</li>
              </ul>
            </div>
          </div>
        </Seccion>

        {/* Gestión de Clientes */}
        <Seccion
          titulo="Gestión de Clientes"
          icono={<Users className="w-6 h-6" />}
          abierta={seccionesAbiertas.gestionClientes}
          onToggle={() => toggleSeccion('gestionClientes')}
        >
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">¿Qué es la Gestión de Clientes?</h4>
              <p className="text-blue-800 text-sm">
                Este módulo te permite administrar toda la información de tus clientes, incluyendo sus datos de contacto, 
                estados de pago, vehículos y mensajes personalizados. Es la base del sistema de cobros.
              </p>
            </div>

            <Paso
              numero="1"
              titulo="Acceder al Módulo"
              descripcion="Desde el menú principal, haz clic en 'Gestión de Clientes' para ver la lista completa de clientes."
            />

            <Paso
              numero="2"
              titulo="Filtrar y Buscar"
              descripcion="Usa los filtros en la parte superior para buscar por estado (OK, DEUDA, CANCELADO, etc.) o por fecha de deuda. También puedes usar la búsqueda global para encontrar clientes específicos."
              consejo="Los filtros se mantienen entre sesiones, así que no perderás tu búsqueda al navegar."
            />

            <Paso
              numero="3"
              titulo="Agregar Nuevo Cliente"
              descripcion="Haz clic en el botón 'Agregar Cliente' para abrir el formulario. Completa todos los campos requeridos y guarda."
              consejo="El campo 'Desde' debe estar en formato MMYYYY (ejemplo: ENERO2024)."
            />

            <Paso
              numero="4"
              titulo="Editar Cliente"
              descripcion="Haz clic en el ícono de editar (lápiz) en la fila del cliente que quieres modificar."
            />

            <Paso
              numero="5"
              titulo="Actualización Masiva"
              descripcion="Para cambiar el estado de múltiples clientes a DEUDA, usa el campo 'Cambiar a DEUDA' ingresando el mes/año (ejemplo: ENERO2024)."
              consejo="Esta acción afectará a todos los clientes con esa fecha de deuda. Confirma antes de proceder."
            />

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-yellow-800 mb-2">
                <AlertCircle className="w-5 h-5" />
                <span className="font-semibold">Estados de Cliente</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span><strong>OK:</strong> Cliente al día</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span><strong>DEUDA:</strong> Cliente con deuda</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                  <span><strong>CANCELADO:</strong> Cliente cancelado</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-black rounded-full"></div>
                  <span><strong>MOROSO:</strong> Cliente en mora</span>
                </div>
              </div>
            </div>
          </div>
        </Seccion>

        {/* Simulaciones GPS */}
        <Seccion
          titulo="Simulaciones GPS"
          icono={<MapPin className="w-6 h-6" />}
          abierta={seccionesAbiertas.simulacionesGPS}
          onToggle={() => toggleSeccion('simulacionesGPS')}
        >
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">¿Qué son las Simulaciones GPS?</h4>
              <p className="text-blue-800 text-sm">
                Las simulaciones GPS te permiten crear rutas virtuales que simulan el movimiento de vehículos en tiempo real 
                o en tiempo pasado. Son útiles para pruebas, demostraciones y análisis de rutas.
              </p>
            </div>

            <Paso
              numero="1"
              titulo="Acceder al Módulo"
              descripcion="Desde el menú principal, selecciona 'Simulaciones GPS' para ver todas las simulaciones existentes."
            />

            <Paso
              numero="2"
              titulo="Crear Nueva Simulación"
              descripcion="Haz clic en 'Nueva Simulación' y completa el formulario con los datos del vehículo, fechas y ruta."
              consejo="Asegúrate de que las fechas de inicio y fin sean coherentes con el tipo de simulación."
            />

            <Paso
              numero="3"
              titulo="Tipos de Simulación"
              descripcion="Elige entre PASTTIME (simulación histórica), REALTIME (tiempo real) o REPLICA (copia de otra simulación)."
            />

            <Paso
              numero="4"
              titulo="Configurar Ruta"
              descripcion="Ingresa las coordenadas de la ruta en formato JSON o usa el generador de rutas para crear una automáticamente."
              consejo="Puedes usar el módulo 'Generar Ruta' para crear rutas automáticas entre dos puntos."
            />

            <Paso
              numero="5"
              titulo="Gestionar Simulaciones"
              descripcion="Usa los botones de editar, duplicar o eliminar según necesites. La duplicación es útil para crear variaciones."
            />

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-800 mb-2">
                <CheckCircle className="w-5 h-5" />
                <span className="font-semibold">Campos Importantes</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <strong>IMEI SIM:</strong> Número del dispositivo simulado
                </div>
                <div>
                  <strong>IMEI Real:</strong> Número del dispositivo real
                </div>
                <div>
                  <strong>Placa:</strong> Matrícula del vehículo
                </div>
                <div>
                  <strong>Estado:</strong> ACTIVO o DESACTIVADO
                </div>
                <div>
                  <strong>Fecha Lanzamiento:</strong> Cuándo se ejecuta la simulación
                </div>
                <div>
                  <strong>Fecha Final:</strong> Cuándo termina la simulación
                </div>
              </div>
            </div>
          </div>
        </Seccion>

        {/* Réplicas GPS */}
        <Seccion
          titulo="Gestión de Réplicas GPS"
          icono={<FileText className="w-6 h-6" />}
          abierta={seccionesAbiertas.replicasGPS}
          onToggle={() => toggleSeccion('replicasGPS')}
        >
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">¿Qué son las Réplicas GPS?</h4>
              <p className="text-blue-800 text-sm">
                Las réplicas son copias de simulaciones existentes que se pueden ejecutar múltiples veces. 
                Son útiles para repetir pruebas o crear variaciones de una simulación original.
              </p>
            </div>

            <Paso
              numero="1"
              titulo="Acceder a Réplicas"
              descripcion="Desde el módulo de Simulaciones GPS, haz clic en 'Ver Réplicas' para acceder a la gestión de réplicas."
            />

            <Paso
              numero="2"
              titulo="Crear Réplica"
              descripcion="Haz clic en el botón de duplicar (dos archivos) en cualquier simulación para crear una réplica."
            />

            <Paso
              numero="3"
              titulo="Actualización Masiva de Fechas"
              descripcion="Selecciona múltiples réplicas usando los checkboxes y usa 'Actualizar Fechas' para cambiar la fecha final de todas a la vez."
              consejo="Esta función es muy útil cuando necesitas reprogramar múltiples réplicas."
            />

            <Paso
              numero="4"
              titulo="Gestionar Réplicas"
              descripcion="Edita, duplica o elimina réplicas según tus necesidades. Las réplicas mantienen la ruta original pero pueden tener fechas diferentes."
            />

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-yellow-800 mb-2">
                <AlertCircle className="w-5 h-5" />
                <span className="font-semibold">Diferencias con Simulaciones</span>
              </div>
              <ul className="text-yellow-700 text-sm space-y-1">
                <li>• Las réplicas son copias de simulaciones existentes</li>
                <li>• Mantienen la ruta original pero pueden tener fechas diferentes</li>
                <li>• Se pueden actualizar masivamente</li>
                <li>• Son ideales para pruebas repetitivas</li>
              </ul>
            </div>
          </div>
        </Seccion>

        {/* Generar Rutas */}
        <Seccion
          titulo="Generar Rutas GPS"
          icono={<Route className="w-6 h-6" />}
          abierta={seccionesAbiertas.generarRutas}
          onToggle={() => toggleSeccion('generarRutas')}
        >
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">¿Qué es el Generador de Rutas?</h4>
              <p className="text-blue-800 text-sm">
                Esta herramienta te permite crear rutas automáticas entre dos puntos usando servicios de navegación reales. 
                Las rutas generadas siguen las calles y caminos reales, no líneas rectas.
              </p>
            </div>

            <Paso
              numero="1"
              titulo="Acceder al Generador"
              descripcion="Desde el menú de Simulaciones GPS, haz clic en 'Generar Ruta'."
            />

            <Paso
              numero="2"
              titulo="Seleccionar Puntos"
              descripcion="Puedes ingresar las coordenadas manualmente o hacer clic en el mapa para seleccionar origen y destino."
              consejo="El primer clic en el mapa será el origen, el segundo será el destino."
            />

            <Paso
              numero="3"
              titulo="Configurar Puntos"
              descripcion="Establece el número de puntos que quieres en la ruta (recomendado: 20). Más puntos = ruta más detallada."
            />

            <Paso
              numero="4"
              titulo="Generar Ruta"
              descripcion="Haz clic en 'Generar Ruta' y espera a que el sistema procese la solicitud."
              consejo="La generación puede tomar unos segundos dependiendo de la distancia y complejidad de la ruta."
            />

            <Paso
              numero="5"
              titulo="Usar la Ruta"
              descripcion="Una vez generada, puedes copiar el formato string o descargar el JSON para usar en simulaciones."
            />

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-800 mb-2">
                <CheckCircle className="w-5 h-5" />
                <span className="font-semibold">Formatos de Salida</span>
              </div>
              <div className="text-sm space-y-2">
                <div>
                  <strong>Formato String:</strong> Coordenadas separadas por comas (lat,lng,lat,lng...)
                </div>
                <div>
                  <strong>Formato JSON:</strong> Array de objetos con latitud, longitud y velocidad
                </div>
                <div>
                  <strong>Visualización:</strong> La ruta se muestra en el mapa para verificación
                </div>
              </div>
            </div>
          </div>
        </Seccion>

        {/* Simulación de Posición */}
        <Seccion
          titulo="Simular Posición GPS"
          icono={<Settings className="w-6 h-6" />}
          abierta={seccionesAbiertas.simulacionPosicion}
          onToggle={() => toggleSeccion('simulacionPosicion')}
        >
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">¿Qué es la Simulación de Posición?</h4>
              <p className="text-blue-800 text-sm">
                Esta herramienta te permite simular la posición de un dispositivo GPS en un punto específico. 
                Es útil para pruebas de localización y verificación de coordenadas.
              </p>
            </div>

            <Paso
              numero="1"
              titulo="Acceder a la Herramienta"
              descripcion="Desde el menú de Simulaciones GPS, selecciona 'Simular Posición'."
            />

            <Paso
              numero="2"
              titulo="Ingresar Coordenadas"
              descripcion="Ingresa la latitud y longitud del punto donde quieres simular la posición."
              consejo="Puedes usar 'Usar Posición Actual' para obtener tu ubicación actual."
            />

            <Paso
              numero="3"
              titulo="Configurar Dispositivo"
              descripcion="Ingresa el IMEI del dispositivo que quieres simular y la placa del vehículo."
            />

            <Paso
              numero="4"
              titulo="Ejecutar Simulación"
              descripcion="Haz clic en 'Simular Posición' para iniciar la simulación."
              consejo="Asegúrate de que las coordenadas sean válidas antes de ejecutar."
            />

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-yellow-800 mb-2">
                <AlertCircle className="w-5 h-5" />
                <span className="font-semibold">Casos de Uso</span>
              </div>
              <ul className="text-yellow-700 text-sm space-y-1">
                <li>• Verificar que un dispositivo está en la ubicación correcta</li>
                <li>• Probar sistemas de localización</li>
                <li>• Simular emergencias o eventos específicos</li>
                <li>• Validar coordenadas antes de crear rutas</li>
              </ul>
            </div>
          </div>
        </Seccion>

        {/* Sistema de Permisos */}
        <Seccion
          titulo="Sistema de Permisos"
          icono={<Shield className="w-6 h-6" />}
          abierta={seccionesAbiertas.sistemaPermisos}
          onToggle={() => toggleSeccion('sistemaPermisos')}
        >
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">¿Qué es el Sistema de Permisos?</h4>
              <p className="text-blue-800 text-sm">
                El sistema de permisos te permite definir qué acciones puede realizar cada usuario en el sistema. 
                Esto asegura que cada usuario solo tenga acceso a las funciones que le corresponden.
              </p>
            </div>

            <Paso
              numero="1"
              titulo="Acceder al Módulo"
              descripcion="Desde el menú principal, selecciona 'Sistema de Permisos' para ver la lista de roles y usuarios."
            />

            <Paso
              numero="2"
              titulo="Crear Nuevo Rol"
              descripcion="Haz clic en 'Nuevo Rol' para definir un nuevo rol de usuario. Completa el nombre y las funcionalidades."
            />

            <Paso
              numero="3"
              titulo="Asignar Permisos"
              descripcion="Para asignar permisos a un rol, selecciona el rol y luego elige las funcionalidades que quieres permitir."
            />

            <Paso
              numero="4"
              titulo="Gestionar Roles"
              descripcion="Edita, duplica o elimina roles según tus necesidades. Los roles definen qué acciones puede realizar un usuario."
            />

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-800 mb-2">
                <CheckCircle className="w-5 h-5" />
                <span className="font-semibold">Campos Importantes</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <strong>Nombre del Rol:</strong> Un nombre descriptivo para el rol
                </div>
                <div>
                  <strong>Funcionalidades:</strong> Lista de acciones permitidas
                </div>
                <div>
                  <strong>Descripción:</strong> Una breve descripción del rol
                </div>
                <div>
                  <strong>Estado:</strong> ACTIVO o DESACTIVADO
                </div>
              </div>
            </div>
          </div>
        </Seccion>

        {/* Gestión de Usuarios */}
        <Seccion
          titulo="Gestión de Usuarios"
          icono={<UserCheck className="w-6 h-6" />}
          abierta={seccionesAbiertas.gestionUsuarios}
          onToggle={() => toggleSeccion('gestionUsuarios')}
        >
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">¿Qué es la Gestión de Usuarios?</h4>
              <p className="text-blue-800 text-sm">
                El módulo de gestión de usuarios te permite administrar los usuarios del sistema, 
                definir sus roles y gestionar sus credenciales.
              </p>
            </div>

            <Paso
              numero="1"
              titulo="Acceder al Módulo"
              descripcion="Desde el menú principal, selecciona 'Gestión de Usuarios' para ver la lista de usuarios."
            />

            <Paso
              numero="2"
              titulo="Agregar Nuevo Usuario"
              descripcion="Haz clic en 'Nuevo Usuario' para crear un nuevo usuario. Completa los datos requeridos (nombre, email, rol, contraseña)."
            />

            <Paso
              numero="3"
              titulo="Editar Usuario"
              descripcion="Haz clic en el ícono de editar (lápiz) en la fila del usuario que quieres modificar."
            />

            <Paso
              numero="4"
              titulo="Actualizar Contraseña"
              descripcion="Para cambiar la contraseña de un usuario, haz clic en el botón 'Actualizar Contraseña' y sigue las instrucciones."
            />

            <Paso
              numero="5"
              titulo="Eliminar Usuario"
              descripcion="Si un usuario ya no necesita acceso, haz clic en el botón de eliminar (cruz) para eliminarlo."
            />

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-yellow-800 mb-2">
                <AlertCircle className="w-5 h-5" />
                <span className="font-semibold">Estados de Usuario</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span><strong>ACTIVO:</strong> Usuario con acceso</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span><strong>INACTIVO:</strong> Usuario sin acceso</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                  <span><strong>ELIMINADO:</strong> Usuario eliminado</span>
                </div>
              </div>
            </div>
          </div>
        </Seccion>

        {/* Configuración del Sistema */}
        <Seccion
          titulo="Configuración del Sistema"
          icono={<Cog className="w-6 h-6" />}
          abierta={seccionesAbiertas.configuracion}
          onToggle={() => toggleSeccion('configuracion')}
        >
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">¿Qué es la Configuración del Sistema?</h4>
              <p className="text-blue-800 text-sm">
                La configuración del sistema te permite ajustar diferentes aspectos de la aplicación, 
                como la interfaz, la seguridad, las notificaciones y la integración con otros servicios.
              </p>
            </div>

            <Paso
              numero="1"
              titulo="Acceder a la Configuración"
              descripcion="Desde el menú principal, selecciona 'Configuración' para ver todas las opciones de configuración."
            />

            <Paso
              numero="2"
              titulo="Personalizar Interfaz"
              descripcion="Puedes cambiar el tema de la aplicación, la fuente, el tamaño de texto y otros aspectos de la interfaz."
            />

            <Paso
              numero="3"
              titulo="Configurar Notificaciones"
              descripcion="Define qué notificaciones quieres recibir y cómo quieres que lleguen (email, notificación push, etc.)."
            />

            <Paso
              numero="4"
              titulo="Integración de Servicios"
              descripcion="Configura la integración con sistemas externos (API, bases de datos, etc.) para que la aplicación funcione de manera más eficiente."
            />

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-800 mb-2">
                <CheckCircle className="w-5 h-5" />
                <span className="font-semibold">Configuraciones Disponibles</span>
              </div>
              <ul className="text-green-700 space-y-1 text-sm">
                <li>• Tema de la aplicación</li>
                <li>• Idioma</li>
                <li>• Notificaciones</li>
                <li>• Integración con mapas</li>
                <li>• Seguridad</li>
                <li>• Exportación de datos</li>
              </ul>
            </div>
          </div>
        </Seccion>

                 {/* FAQ */}
         <Seccion
           titulo="FAQ (Preguntas Frecuentes)"
           icono={<MessageCircle className="w-6 h-6" />}
           abierta={seccionesAbiertas.faq}
           onToggle={() => toggleSeccion('faq')}
         >
           <div className="space-y-6">
             <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
               <h4 className="font-semibold text-blue-900 mb-2">¿Cómo puedo cambiar mi contraseña?</h4>
               <p className="text-blue-800 text-sm">
                 Ve a la sección de "Gestión de Usuarios" y selecciona el usuario. Haz clic en "Actualizar Contraseña" y sigue las instrucciones.
               </p>
             </div>

             <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
               <h4 className="font-semibold text-blue-900 mb-2">¿Qué son las réplicas GPS?</h4>
               <p className="text-blue-800 text-sm">
                 Las réplicas son copias de simulaciones existentes que se pueden ejecutar múltiples veces. 
                 Son útiles para repetir pruebas o crear variaciones de una simulación original.
               </p>
             </div>

             <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
               <h4 className="font-semibold text-blue-900 mb-2">¿Cómo puedo exportar los datos de un cliente?</h4>
               <p className="text-blue-800 text-sm">
                 Ve a la sección de "Gestión de Clientes" y selecciona el cliente. Haz clic en "Exportar Datos" para descargar un archivo CSV.
               </p>
             </div>

             <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
               <h4 className="font-semibold text-blue-900 mb-2">¿Qué hago si olvidé mi contraseña?</h4>
               <p className="text-blue-800 text-sm">
                 Contacta al administrador del sistema para que pueda restablecer tu contraseña. 
                 Por seguridad, no se pueden recuperar contraseñas olvidadas.
               </p>
             </div>

             <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
               <h4 className="font-semibold text-blue-900 mb-2">¿Cómo funciona la actualización masiva?</h4>
               <p className="text-blue-800 text-sm">
                 La actualización masiva te permite cambiar el estado de múltiples registros a la vez. 
                 Selecciona los registros y especifica el nuevo estado. Confirma antes de proceder.
               </p>
             </div>

             <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
               <h4 className="font-semibold text-blue-900 mb-2">¿Puedo usar el sistema desde mi móvil?</h4>
               <p className="text-blue-800 text-sm">
                 Sí, el sistema es completamente responsive y funciona en dispositivos móviles. 
                 La interfaz se adapta automáticamente al tamaño de la pantalla.
               </p>
             </div>

             <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
               <div className="flex items-center gap-2 text-yellow-800 mb-2">
                 <AlertCircle className="w-5 h-5" />
                 <span className="font-semibold">Preguntas Frecuentes</span>
               </div>
               <ul className="text-yellow-700 text-sm space-y-1">
                 <li>• ¿Cómo puedo cambiar mi contraseña?</li>
                 <li>• ¿Qué son las réplicas GPS?</li>
                 <li>• ¿Cómo puedo exportar los datos de un cliente?</li>
                 <li>• ¿Qué hago si olvidé mi contraseña?</li>
                 <li>• ¿Cómo funciona la actualización masiva?</li>
                 <li>• ¿Puedo usar el sistema desde mi móvil?</li>
               </ul>
             </div>
           </div>
         </Seccion>

                 {/* Glosario de Términos */}
         <Seccion
           titulo="Glosario de Términos"
           icono={<Search className="w-6 h-6" />}
           abierta={seccionesAbiertas.glosario}
           onToggle={() => toggleSeccion('glosario')}
         >
           <div className="space-y-6">
             <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
               <h4 className="font-semibold text-blue-900 mb-2">¿Qué es un IMEI?</h4>
               <p className="text-blue-800 text-sm">
                 IMEI (International Mobile Equipment Identity) es un número único que identifica de manera exclusiva un dispositivo móvil. 
                 Es utilizado para la autenticación y el seguimiento de dispositivos.
               </p>
             </div>

             <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
               <h4 className="font-semibold text-blue-900 mb-2">¿Qué es un JSON?</h4>
               <p className="text-blue-800 text-sm">
                 JSON (JavaScript Object Notation) es un formato de intercambio de datos ligero y fácil de leer. 
                 Es ampliamente utilizado para transmitir datos entre sistemas.
               </p>
             </div>

             <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
               <h4 className="font-semibold text-blue-900 mb-2">¿Qué es un API?</h4>
               <p className="text-blue-800 text-sm">
                 API (Application Programming Interface) es un conjunto de reglas y protocolos que permite la comunicación entre diferentes aplicaciones. 
                 Tecnytte Admin utiliza APIs para interactuar con servicios externos.
               </p>
             </div>

             <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
               <h4 className="font-semibold text-blue-900 mb-2">¿Qué es GPS?</h4>
               <p className="text-blue-800 text-sm">
                 GPS (Global Positioning System) es un sistema de navegación por satélite que proporciona información de ubicación y tiempo. 
                 Es fundamental para las simulaciones de rutas en el sistema.
               </p>
             </div>

             <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
               <h4 className="font-semibold text-blue-900 mb-2">¿Qué es una Simulación?</h4>
               <p className="text-blue-800 text-sm">
                 Una simulación es la recreación de un proceso o sistema real en un entorno controlado. 
                 En el contexto de GPS, simula el movimiento de vehículos a través de rutas específicas.
               </p>
             </div>

             <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
               <h4 className="font-semibold text-blue-900 mb-2">¿Qué es una Réplica?</h4>
               <p className="text-blue-800 text-sm">
                 Una réplica es una copia exacta de una simulación existente que puede ser ejecutada múltiples veces 
                 con diferentes parámetros o fechas.
               </p>
             </div>

             <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
               <div className="flex items-center gap-2 text-yellow-800 mb-2">
                 <AlertCircle className="w-5 h-5" />
                 <span className="font-semibold">Glosario de Términos</span>
               </div>
               <ul className="text-yellow-700 text-sm space-y-1">
                 <li>• IMEI: Identificador único de dispositivo móvil</li>
                 <li>• JSON: Formato de intercambio de datos</li>
                 <li>• API: Interfaz de programación de aplicaciones</li>
                 <li>• GPS: Sistema de posicionamiento global</li>
                 <li>• Simulación: Recreación de procesos reales</li>
                 <li>• Réplica: Copia de una simulación existente</li>
               </ul>
             </div>
           </div>
         </Seccion>

        {/* Consejos y Trucos */}
        <Seccion
          titulo="Consejos y Trucos"
          icono={<HelpCircle className="w-6 h-6" />}
          abierta={seccionesAbiertas.consejos}
          onToggle={() => toggleSeccion('consejos')}
        >
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">Productividad</h4>
                <ul className="text-blue-800 text-sm space-y-1">
                  <li>• Usa los filtros para encontrar rápidamente lo que buscas</li>
                  <li>• Aprovecha la actualización masiva para cambios múltiples</li>
                  <li>• Duplica simulaciones en lugar de crear desde cero</li>
                  <li>• Guarda rutas frecuentes para reutilizar</li>
                </ul>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2">Navegación</h4>
                <ul className="text-green-800 text-sm space-y-1">
                  <li>• Usa el breadcrumb para navegar rápidamente</li>
                  <li>• Los filtros se mantienen entre páginas</li>
                  <li>• Usa Ctrl+F para búsqueda rápida en tablas</li>
                  <li>• Los tooltips te dan información adicional</li>
                </ul>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-900 mb-2">Datos</h4>
                <ul className="text-yellow-800 text-sm space-y-1">
                  <li>• Siempre valida las coordenadas antes de usar</li>
                  <li>• Usa formatos de fecha consistentes</li>
                  <li>• Haz respaldos antes de cambios masivos</li>
                  <li>• Verifica los permisos antes de realizar acciones</li>
                </ul>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-purple-900 mb-2">Solución de Problemas</h4>
                <ul className="text-purple-800 text-sm space-y-1">
                  <li>• Si la página no carga, recarga el navegador</li>
                  <li>• Verifica tu conexión a internet</li>
                  <li>• Contacta al administrador si tienes problemas de permisos</li>
                  <li>• Usa el botón "Limpiar Filtros" si algo no funciona</li>
                </ul>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-red-800 mb-2">
                <AlertCircle className="w-5 h-5" />
                <span className="font-semibold">Importante</span>
              </div>
              <ul className="text-red-700 text-sm space-y-1">
                <li>• Siempre confirma antes de eliminar registros</li>
                <li>• Los cambios masivos no se pueden deshacer</li>
                <li>• Mantén tus credenciales seguras</li>
                <li>• Cierra sesión cuando termines de trabajar</li>
              </ul>
            </div>
          </div>
        </Seccion>

        {/* Contacto y Soporte */}
        <div className="bg-white rounded-lg shadow p-6 mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <HelpCircle className="w-5 h-5" />
            ¿Necesitas Ayuda?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Soporte Técnico</h4>
              <p className="text-blue-800 text-sm mb-2">
                Si encuentras algún problema o tienes preguntas específicas:
              </p>
              <ul className="text-blue-700 text-sm space-y-1">
                <li>• Contacta al administrador del sistema</li>
                <li>• Revisa los logs de error en la consola del navegador</li>
                <li>• Verifica que tienes los permisos necesarios</li>
                <li>• Consulta la documentación técnica</li>
              </ul>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-semibold text-green-900 mb-2">Mejoras Sugeridas</h4>
              <p className="text-green-800 text-sm mb-2">
                Para sugerir mejoras o reportar bugs:
              </p>
              <ul className="text-green-700 text-sm space-y-1">
                <li>• Documenta el problema con capturas de pantalla</li>
                <li>• Describe los pasos para reproducir el problema</li>
                <li>• Incluye información del navegador y sistema operativo</li>
                <li>• Proporciona ejemplos específicos</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Información de Contacto */}
        <div className="bg-white rounded-lg shadow p-6 mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Info className="w-5 h-5" />
            Información de Contacto
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="font-semibold text-purple-900 mb-2">Desarrollo</h4>
              <p className="text-purple-800 text-sm mb-2">
                Para consultas técnicas y desarrollo:
              </p>
              <ul className="text-purple-700 text-sm space-y-1">
                <li>• Email: desarrollo@tecnytte.com</li>
                <li>• Teléfono: +51979964575</li>
                <li>• Horario: Lunes a Viernes 9:00-18:00</li>
              </ul>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h4 className="font-semibold text-orange-900 mb-2">Soporte</h4>
              <p className="text-orange-800 text-sm mb-2">
                Para soporte técnico inmediato:
              </p>
              <ul className="text-orange-700 text-sm space-y-1">
                <li>• Email: soporte@tecnytte.com</li>
                <li>• Teléfono: +51979964575</li>
                <li>• Horario: 24/7</li>
              </ul>
            </div>
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h4 className="font-semibold text-indigo-900 mb-2">Administración</h4>
              <p className="text-indigo-800 text-sm mb-2">
                Para consultas administrativas:
              </p>
              <ul className="text-indigo-700 text-sm space-y-1">
                <li>• Email: ventas@tecnytte.com</li>
                <li>• Teléfono: +51979964575</li>
                <li>• Horario: Lunes a Viernes 8:00-17:00</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Recursos Adicionales */}
        <div className="bg-white rounded-lg shadow p-6 mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Recursos Adicionales
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
              <h4 className="font-semibold text-teal-900 mb-2">Documentación</h4>
              <ul className="text-teal-700 text-sm space-y-1">
                <li>• Guía de instalación</li>
                <li>• Manual de administración</li>
                <li>• API Documentation</li>
                <li>• Changelog</li>
              </ul>
            </div>
            <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
              <h4 className="font-semibold text-pink-900 mb-2">Enlaces Útiles</h4>
              <ul className="text-pink-700 text-sm space-y-1">
                <li>• Sitio web oficial</li>
                <li>• Portal de desarrolladores</li>
                <li>• Comunidad de usuarios</li>
                <li>• Blog de actualizaciones</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ManualUsuario 